import { z } from 'zod'
import { coverageRuleInputSchema, coverageRuleSchema } from './coverage'
import { libraryLayoutSchema } from './library-columns'
import { streamFiltersSchema } from './stream'
import { EMPTY_STREAM_BRANDING, streamBrandingSchema } from './stream-branding'
import {
  STREAM_ROLLUP_MAX,
  streamBroadcastSourceSchema,
  streamModeSchema,
  streamReachModelSchema,
} from './stream-build'

/**
 * A workspace's stream configuration (CLI-758): what feeds the stream, how the analytics build
 * makes it, and how Library and Analytics show it — the workspace's, kept on the server, so
 * everyone with access to the account sees what someone with edit on it set.
 */

// ── The pages' shared settings (CLI-1009) ────────────────────────────────────────────────────

/** The facets whose values can be grouped. */
export const GROUPABLE_FACETS = ['outlets', 'topics', 'tags'] as const
export type GroupableFacet = (typeof GROUPABLE_FACETS)[number]

/** A named set of one facet's values — one click toggles the whole set, on both pages. */
export const facetValueGroupSchema = z.object({
  id: z.string().min(1).max(80),
  facet: z.enum(GROUPABLE_FACETS),
  name: z.string().trim().min(1).max(80),
  values: z.array(z.string().max(300)).max(500),
})
export type FacetValueGroup = z.infer<typeof facetValueGroupSchema>

/** A workspace's value groups. */
export const facetGroupsSchema = z.array(facetValueGroupSchema).max(200)

/** An Analytics tab's saved filters, by name. */
export const savedFilterViewSchema = z.object({
  id: z.string().min(1).max(80),
  name: z.string().trim().min(1).max(120),
  filters: streamFiltersSchema,
})
export type SavedFilterView = z.infer<typeof savedFilterViewSchema>

/** One Analytics tab's saved filter views and the one it opens on (null = the default filters). */
export const tabFilterViewsSchema = z.object({
  views: z.array(savedFilterViewSchema).max(50),
  defaultViewId: z.string().nullable(),
})
export type TabFilterViews = z.infer<typeof tabFilterViewsSchema>

/** Every Analytics tab's saved filter views, by dashboard tab id. */
export const analyticsFilterViewsSchema = z.record(z.string().min(1).max(80), tabFilterViewsSchema)
export type AnalyticsFilterViews = z.infer<typeof analyticsFilterViewsSchema>

// ── Stream configuration ──────────────────────────────────────────────────────────────────────

/**
 * A topic feeding the stream — the report header junction with the per-topic config that used to
 * be stream-wide (CLI-758). Empty `dataSources` = every source the topic collects;
 * `includeSyndications` and `coverage` are this topic's own. `coverage` is the INPUT shape here
 * (rules without ids); the stored config returns `streamTopicConfigSchema`, whose coverage carries
 * ids. A field left at its default (true / no rules) means the stream's own default applies.
 */
export const streamTopicJunctionSchema = z.object({
  topicId: z.uuid(),
  dataSources: z.array(z.string().min(1)).max(10).default([]),
  /** Pull this topic's syndicated copies into the stream. Default true (the stream's own default). */
  includeSyndications: z.boolean().default(true),
  /** This topic's own coverage rules on the stream — outlets, countries, languages — which replace
   *  the stream's per field, the way a report header overrides its report. Empty = inherit. */
  coverage: z.array(coverageRuleInputSchema).default([]),
})
export type StreamTopicJunction = z.infer<typeof streamTopicJunctionSchema>
/** What a caller sends: `dataSources`, `includeSyndications` and `coverage` may be left out. */
export type StreamTopicJunctionInput = z.input<typeof streamTopicJunctionSchema>

/** A topic junction as the stored config returns it: coverage rules carry their ids. */
export const streamTopicConfigSchema = streamTopicJunctionSchema.extend({
  coverage: z.array(coverageRuleSchema),
})
export type StreamTopicConfig = z.infer<typeof streamTopicConfigSchema>

/**
 * What the API stores and returns: rules carry their ids, like a report's. `coverage` is the
 * stream's own DEFAULT rules; each topic in `topics` carries its own `coverage` override and
 * `includeSyndications`. `includeSyndications` at this level is the stream-wide default the read
 * path falls back to for rows no attached topic claims (CLI-758).
 */
export const streamConfigSchema = z.object({
  topics: z.array(streamTopicConfigSchema),
  coverage: z.array(coverageRuleSchema),
  mode: streamModeSchema,
  /** The stream-wide syndication default — applied to rows not attributable to an attached topic
   *  (every row until the cache job stamps topic ids). A topic's own `includeSyndications` wins for
   *  its rows. Distinct from the pages' "Group syndication" toggle, which only collapses display. */
  includeSyndications: z.boolean(),
  /** The workspace's colours for its topics, tags and classifications (`streamBrandingSchema`). */
  branding: streamBrandingSchema,
  /** How the build estimates reach; null = the client's default. */
  reachModel: streamReachModelSchema.nullable(),
  /** Which broadcast clips the build takes; null = the client's default. */
  broadcastSource: streamBroadcastSourceSchema.nullable(),
  /**
   * Other sub-accounts of the same account whose coverage this stream also includes (TD Bank -
   * General over TD's lines of business). Each member keeps its own topics and coverage rules;
   * this stream's mode, reach and broadcast settings apply to the whole. One level: a member's own
   * rollup is not followed.
   */
  rollupSubaccountIds: z.array(z.uuid()),
  /**
   * The workspace's Library (CLI-1009): its columns in order, sizes and density — the same for
   * everyone who opens it, and the build columns it shows are the ones both pages filter by.
   * Null = the default layout. Changing it needs edit.
   */
  libraryLayout: libraryLayoutSchema.nullable(),
  /** Each Analytics tab's saved filter views and the one it opens on. */
  analyticsFilterViews: analyticsFilterViewsSchema,
  /** The named value groups in the filter bar's facets, on both pages. */
  facetGroups: facetGroupsSchema,
})
export type StreamConfig = z.infer<typeof streamConfigSchema>

export const EMPTY_STREAM_CONFIG: StreamConfig = {
  topics: [],
  coverage: [],
  mode: 'reports',
  includeSyndications: true,
  branding: EMPTY_STREAM_BRANDING,
  reachModel: null,
  broadcastSource: null,
  rollupSubaccountIds: [],
  libraryLayout: null,
  analyticsFilterViews: {},
  facetGroups: [],
}

/**
 * `PUT /api/streams/:subaccountId/config` — the whole configuration, replaced. The syndication
 * rule is now per topic (on each junction), so this no longer carries a stream-wide
 * `includeSyndications`: the stored stream default is frozen and only read as the fallback.
 */
export const streamConfigInputSchema = z.object({
  topics: z.array(streamTopicJunctionSchema).max(200),
  coverage: z.array(coverageRuleInputSchema).max(500),
  /** Left out, the stored mode stands, like every setting below. */
  mode: streamModeSchema.optional(),
  /** Left out, the stored colours stand — a caller that does not know about them never wipes
   *  them. */
  branding: streamBrandingSchema.optional(),
  /** The three build settings below: left out, the stored value stands, so a caller that never
   *  touched one never writes it (null is a value — the client's default). */
  reachModel: streamReachModelSchema.nullable().optional(),
  broadcastSource: streamBroadcastSourceSchema.nullable().optional(),
  rollupSubaccountIds: z.array(z.uuid()).max(STREAM_ROLLUP_MAX).optional(),
  libraryLayout: libraryLayoutSchema.nullable().optional(),
  analyticsFilterViews: analyticsFilterViewsSchema.optional(),
  facetGroups: facetGroupsSchema.optional(),
})
export type StreamConfigInput = z.infer<typeof streamConfigInputSchema>
